import React, { useContext, useEffect } from 'react';
import type { PropsWithChildren } from 'react';
import {
  ChannelListMessenger,
  ChannelListMessengerProps,
} from 'stream-chat-react';
import { ContextualChatContext } from 'contexts';
import { useQuery } from '@tanstack/react-query';

const CustomChannelListContainer = (
  props: PropsWithChildren<ChannelListMessengerProps>,
) => {
  const { channelIdToTop, setChannelIdToTop, client } = useContext(
    ContextualChatContext,
  );
  const { loadedChannels, children, setChannels, loading } = props;

  const { data: channelToTop } = useQuery({
    queryKey: ['channelToReorder', channelIdToTop],
    queryFn: async () => {
      if (!channelIdToTop) return null;
      const [channel] = await client.queryChannels(
        { cid: channelIdToTop },
        {},
        { limit: 1, watch: true },
      );
      return channel;
    },
    enabled: !!channelIdToTop,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!channelIdToTop || loading) return;

    const targetChannelIndex = loadedChannels?.findIndex(
      loadedChannel => loadedChannel.cid === channelIdToTop,
    );

    // Reorder if the channel is already loaded locally
    if (targetChannelIndex > 0) {
      setChannels(channels => [
        channels[targetChannelIndex],
        ...channels.filter(ch => ch.data?.cid !== channelIdToTop),
      ]);
      setChannelIdToTop(null);
    }
    // If not found locally, useQuery will handle fetching it
    else if (targetChannelIndex === -1 && channelToTop) {
      setChannels(channels => [
        channelToTop,
        ...channels.filter(ch => ch.data?.cid !== channelIdToTop),
      ]);
      setChannelIdToTop(null);
    }
  }, [channelIdToTop, channelToTop]);

  return <ChannelListMessenger {...props}>{children}</ChannelListMessenger>;
};

export default CustomChannelListContainer;
